import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  Facility,
  FacilityRequest,
  FacilityUpdate,
  HealthcareFacilityStats,
} from "../../types/facility.definition";

// Initial state
interface FacilityState {
  facilities: Facility[];
  selectedFacility: Facility | null;
  facilityStats: HealthcareFacilityStats | null;
  loading: boolean;
  error: string | null;
  lastFetch: number | null;
  searchResults: {
    facilities: Facility[];
    totalCount: number;
    hasMore: boolean;
  } | null;
}

const initialState: FacilityState = {
  facilities: [],
  selectedFacility: null,
  facilityStats: null,
  loading: false,
  error: null,
  lastFetch: null,
  searchResults: null,
};

export const fetchFacilitiesSuccess = createAction<Facility[]>(
  "facilities/fetchFacilitiesSuccess"
);
export const fetchFacilitiesFailure = createAction<string>(
  "facilities/fetchFacilitiesFailure"
);

export const searchFacilitiesRequest = createAction<{
  keyword?: string;
  offset?: number;
  limit?: number;
  filters?: {
    facilityType?: string;
    status?: string;
    isPartner?: string;
  };
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}>("facilities/searchFacilitiesRequest");
export const searchFacilitiesSuccess = createAction<{
  facilities: Facility[];
  totalCount: number;
  hasMore: boolean;
}>("facilities/searchFacilitiesSuccess");
export const searchFacilitiesFailure = createAction<string>(
  "facilities/searchFacilitiesFailure"
);

export const fetchFacilityByIdRequest = createAction<string>(
  "facilities/fetchFacilityByIdRequest"
);
export const fetchFacilityByIdSuccess = createAction<Facility>(
  "facilities/fetchFacilityByIdSuccess"
);
export const fetchFacilityByIdFailure = createAction<string>(
  "facilities/fetchFacilityByIdFailure"
);

export const fetchFacilityStatsRequest = createAction<string>(
  "facilities/fetchFacilityStatsRequest"
);
export const fetchFacilityStatsSuccess = createAction<HealthcareFacilityStats>(
  "facilities/fetchFacilityStatsSuccess"
);
export const fetchFacilityStatsFailure = createAction<string>(
  "facilities/fetchFacilityStatsFailure"
);

export const createFacilityRequest = createAction<FacilityRequest>(
  "facilities/createFacilityRequest"
);
export const createFacilitySuccess = createAction<Facility>(
  "facilities/createFacilitySuccess"
);
export const createFacilityFailure = createAction<string>(
  "facilities/createFacilityFailure"
);

// MED-1466: the edit modal is unmounted by its parent the moment it dispatches,
// so it can never observe the outcome from the store. The saga calls these back
// instead, which is how the app-notice slice already reports a save.
export const updateFacilityRequest = createAction<{
  facilityId: string;
  data: FacilityUpdate;
  onSuccess?: () => void;
  onError?: () => void;
}>("facilities/updateFacilityRequest");
export const updateFacilitySuccess = createAction<Facility>(
  "facilities/updateFacilitySuccess"
);
export const updateFacilityFailure = createAction<string>(
  "facilities/updateFacilityFailure"
);

// Slice
const facilitySlice = createSlice({
  name: "facilities",
  initialState,
  reducers: {
    setSelectedFacility: (state, action: PayloadAction<Facility | null>) => {
      state.selectedFacility = action.payload;
    },
    clearFacilities: state => {
      state.facilities = [];
      state.selectedFacility = null;
      state.facilityStats = null;
      state.lastFetch = null;
    },
    updateFacilityInList: (state, action: PayloadAction<Facility>) => {
      const index = state.facilities.findIndex(
        facility => facility.id === action.payload.id
      );
      if (index !== -1) {
        state.facilities[index] = action.payload;
      }
      // Update selected facility if it's the same
      if (state.selectedFacility?.id === action.payload.id) {
        state.selectedFacility = action.payload;
      }
    },
    removeFacilityFromList: (state, action: PayloadAction<number>) => {
      state.facilities = state.facilities.filter(
        facility => facility.id !== action.payload
      );

      if (state.selectedFacility?.id === action.payload) {
        state.selectedFacility = null;
      }
    },
    clearError: state => {
      state.error = null;
    },
  },
  extraReducers: builder => {
    // Fetch facilities (handled by searchFacilitiesSaga)
    builder

      .addCase(fetchFacilitiesSuccess, (state, action) => {
        state.loading = false;
        state.facilities = action.payload;
        state.lastFetch = Date.now();
        state.error = null;
      })
      .addCase(fetchFacilitiesFailure, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Search facilities
    builder
      .addCase(searchFacilitiesRequest, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchFacilitiesSuccess, (state, action) => {
        state.loading = false;
        state.searchResults = action.payload;
        state.lastFetch = Date.now();
        state.error = null;
      })
      .addCase(searchFacilitiesFailure, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch facility by ID
    builder
      .addCase(fetchFacilityByIdRequest, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFacilityByIdSuccess, (state, action) => {
        state.loading = false;
        state.selectedFacility = action.payload;
        state.error = null;

        // Update or add to facilities list
        const existingIndex = state.facilities.findIndex(
          facility => facility.id === action.payload.id
        );
        if (existingIndex !== -1) {
          state.facilities[existingIndex] = action.payload;
        } else {
          state.facilities.push(action.payload);
        }
      })
      .addCase(fetchFacilityByIdFailure, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch facility stats
    builder
      .addCase(fetchFacilityStatsRequest, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFacilityStatsSuccess, (state, action) => {
        state.loading = false;
        state.facilityStats = action.payload;
        state.error = null;
      })
      .addCase(fetchFacilityStatsFailure, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Create facility
    builder
      .addCase(createFacilityRequest, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createFacilitySuccess, (state, action) => {
        state.loading = false;
        state.facilities.push(action.payload);
        state.error = null;
      })
      .addCase(createFacilityFailure, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update facility
    builder
      .addCase(updateFacilityRequest, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateFacilitySuccess, (state, action) => {
        state.loading = false;
        const index = state.facilities.findIndex(
          facility => facility.id === action.payload.id
        );
        if (index !== -1) {
          state.facilities[index] = action.payload;
        }
        // Update selected facility if it's the same
        if (state.selectedFacility?.id === action.payload.id) {
          state.selectedFacility = action.payload;
        }
        state.error = null;
      })
      .addCase(updateFacilityFailure, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

// Export slice actions
export const {
  setSelectedFacility,
  clearFacilities,
  updateFacilityInList,
  removeFacilityFromList,
  clearError,
} = facilitySlice.actions;

// Export selectors
export const selectFacilities = (state: { facilities: FacilityState }) =>
  state.facilities.facilities;
export const selectSelectedFacility = (state: { facilities: FacilityState }) =>
  state.facilities.selectedFacility;
export const selectFacilityStats = (state: { facilities: FacilityState }) =>
  state.facilities.facilityStats;
export const selectFacilitiesLoading = (state: { facilities: FacilityState }) =>
  state.facilities.loading;
export const selectFacilitiesError = (state: { facilities: FacilityState }) =>
  state.facilities.error;
export const selectLastFetch = (state: { facilities: FacilityState }) =>
  state.facilities.lastFetch;
export const selectSearchResults = (state: { facilities: FacilityState }) =>
  state.facilities.searchResults;

// Helper selector to check if data is stale (older than 5 minutes)
export const selectIsDataStale = (state: { facilities: FacilityState }) => {
  if (!state.facilities.lastFetch) return true;
  return Date.now() - state.facilities.lastFetch > 5 * 60 * 1000; // 5 minutes
};

export default facilitySlice.reducer;
